// 白板 (the shared whiteboard): what the office (src/office/whiteboard.ts) and the page (public/whiteboard.js) agree on.
// Pure, no DOM: the boards there are, who may use one, the palettes, the limits, and the one check every element goes
// through (clean). The office bundles this file (as it does fishing-core.js), so both sides judge with the same code.
//
// Boards: one per 会议室 (a map zone with meeting = true; its id is the zone's id) and the 白板 on the open deck (the
// map's "whiteboards" layer, deck/map.js; ids wb-*). A room's board is used by whoever is in the room; a deck board
// by whoever stands within NEAR tiles of it (by the feet, as the move rules see them) on the open deck.
//
// An element, as stored and sent (board units: 1 unit is a CSS pixel at zoom 1; the board is unbounded within ±COORD):
//   common  {id, k, x, y, c, z, by}  id: made by the page that drew it (ID); k: the kind; (x, y) where it is; c: its
//           colour (one of the palettes); z: its order (the office numbers new elements); by: who made it (a name)
//   path    {pts: [dx0, dy0, dx1, dy1, ...] (relative to x, y; at most PTS_MAX points), w: one of SIZES}   a pen stroke
//   note    {w, h, text}                                     a sticky note (colour from NOTE_COLORS)
//   text    {text, s: one of TEXT_SIZES}                     a line or a few of text
//   rect / ellipse {w, h, sw: one of SIZES}                  an outline (w, h > 0)
//   arrow   {dx, dy, sw}                                     from (x, y) to (x + dx, y + dy)
// Images are not in v1 (see docs/architecture.md "The shared whiteboard").

/** The pen's 6 colours (and the shapes' and text's), and the sticky notes' 5. Their names: the page's wb.color.<name>. */
export const PEN_COLORS = ['#1b1410', '#e03131', '#1c7ed6', '#2f9e44', '#f08c00', '#7048e8'];
export const PEN_NAMES = ['black', 'red', 'blue', 'green', 'orange', 'purple'];
export const NOTE_COLORS = ['#ffe066', '#ffc9c9', '#a5d8ff', '#b2f2bb', '#e5dbff'];
export const NOTE_NAMES = ['yellow', 'pink', 'blue', 'green', 'purple'];
/** Colour names agents may use (deck board note --color yellow). */
export const NOTE_BY_NAME = { yellow: 0, pink: 1, blue: 2, green: 3, purple: 4 };
/** The pen's 3 sizes (also the shapes' stroke widths), and text's 3 sizes. */
export const SIZES = [3, 6, 14];
export const TEXT_SIZES = [18, 28, 44];
export const NOTE_W = 180, NOTE_H = 150;

/** How close (tiles, feet to the board's base) you must stand to use a board on the open deck. */
export const NEAR = 2.6;
/** Limits. The office refuses what is over them (and says why: wb_err). */
export const LIMITS = {
  elements: 1500,          // elements on one board
  boardBytes: 1_200_000,   // the board's elements as JSON, all together
  elBytes: 16_000,         // one element as JSON
  ptsMax: 1000,            // points in one pen stroke (the page starts a new stroke after that)
  text: 500,               // characters in a note or a text
  coord: 20_000,           // |x|, |y| (and a path's offsets, a shape's size)
  opsPerMsg: 64,           // operations in one wb_op message
  msgBytes: 96_000,        // one wb_op message as JSON
};
export const ID = /^[A-Za-z0-9_-]{4,24}$/;
const KINDS = new Set(['path', 'note', 'text', 'rect', 'ellipse', 'arrow']);

/** Every board in the office: [{id, name, kind: 'room' | 'deck', zone?, x?, y?}] (rooms first, as the map lists them). */
export function boardsOf(MAP) {
  const rooms = (MAP.zones || []).filter((z) => z.meeting).map((z) => ({ id: z.id, name: z.name, kind: 'room', zone: z.id }));
  const deck = (MAP.whiteboards || []).map((b) => ({ id: b.id, name: b.name, kind: 'deck', x: b.x, y: b.y }));
  return [...rooms, ...deck];
}
/** Distance (tiles) from an otter's feet at position (x, y) to a deck board's base. */
export const boardDist = (b, x, y) => Math.hypot(x + 0.5 - b.x, y + 0.8 - b.y);
/** May the otter at (x, y) use board b? zoneIdAt(x, y): the id of the room it's in, or null (the office's zoneAt). */
export function canUse(b, x, y, zoneIdAt) {
  if (!b) return false;
  const z = zoneIdAt(x, y);
  return b.kind === 'room' ? z === b.zone : !z && boardDist(b, x, y) <= NEAR;
}
/** The deck board within reach of (x, y), nearest first, or null. */
export function deckBoardNear(boards, x, y, zoneIdAt) {
  let best = null, bd = Infinity;
  for (const b of boards) if (b.kind === 'deck' && canUse(b, x, y, zoneIdAt)) { const d = boardDist(b, x, y); if (d < bd) { bd = d; best = b; } }
  return best;
}

const num = (v) => typeof v === 'number' && Number.isFinite(v);
const coord = (v) => num(v) && Math.abs(v) <= LIMITS.coord;
const r1 = (v) => Math.round(v * 10) / 10;
/** Text as stored: a string, control characters out (newlines and tabs stay), at most LIMITS.text code points. */
export function cleanText(v) {
  if (typeof v !== 'string') return null;
  // eslint-disable-next-line no-control-regex
  const s = v.replace(/\r\n?/g, '\n').replace(/[\u0000-\u0008\u000b-\u001f\u007f\u2028\u2029]/g, '');
  const cps = [...s];
  return cps.length > LIMITS.text ? cps.slice(0, LIMITS.text).join('') : s;
}

/**
 * One element from a page (or an agent), checked: the element to keep (only the fields of its kind, numbers rounded)
 * or null. `z` and `by` are the office's to set, so they're dropped here.
 */
export function clean(e) {
  if (!e || typeof e !== 'object' || typeof e.id !== 'string' || !ID.test(e.id) || !KINDS.has(e.k)) return null;
  if (!coord(e.x) || !coord(e.y)) return null;
  const base = { id: e.id, k: e.k, x: r1(e.x), y: r1(e.y) };
  if (e.k === 'note') {
    const text = cleanText(e.text ?? ''); if (text == null || !NOTE_COLORS.includes(e.c)) return null;
    const w = num(e.w) ? Math.max(80, Math.min(480, Math.round(e.w))) : NOTE_W, h = num(e.h) ? Math.max(60, Math.min(480, Math.round(e.h))) : NOTE_H;
    return { ...base, c: e.c, w, h, text };
  }
  if (!PEN_COLORS.includes(e.c)) return null;
  if (e.k === 'text') {
    const text = cleanText(e.text ?? ''); if (!text || !text.trim()) return null;
    return { ...base, c: e.c, s: TEXT_SIZES.includes(e.s) ? e.s : TEXT_SIZES[0], text };
  }
  if (e.k === 'path') {
    const p = e.pts;
    if (!Array.isArray(p) || p.length < 2 || p.length % 2 || p.length > LIMITS.ptsMax * 2 || !p.every(coord)) return null;
    return { ...base, c: e.c, w: SIZES.includes(e.w) ? e.w : SIZES[0], pts: p.map(r1) };
  }
  const sw = SIZES.includes(e.sw) ? e.sw : SIZES[0];
  if (e.k === 'arrow') {
    if (!coord(e.dx) || !coord(e.dy) || Math.hypot(e.dx, e.dy) < 2) return null;
    return { ...base, c: e.c, sw, dx: r1(e.dx), dy: r1(e.dy) };
  }
  // rect / ellipse
  if (!coord(e.w) || !coord(e.h) || e.w < 2 || e.h < 2) return null;
  return { ...base, c: e.c, sw, w: r1(e.w), h: r1(e.h) };
}

/** An element's box {x, y, w, h} in board units (a path's and an arrow's around their points; a text's estimated). */
export function bounds(e) {
  if (e.k === 'path') {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < e.pts.length; i += 2) { const x = e.pts[i], y = e.pts[i + 1]; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    const h = e.w / 2;
    return { x: e.x + x0 - h, y: e.y + y0 - h, w: x1 - x0 + e.w, h: y1 - y0 + e.w };
  }
  if (e.k === 'arrow') return { x: e.x + Math.min(0, e.dx) - e.sw, y: e.y + Math.min(0, e.dy) - e.sw, w: Math.abs(e.dx) + 2 * e.sw, h: Math.abs(e.dy) + 2 * e.sw };
  if (e.k === 'text') {
    const lines = String(e.text).split('\n');
    return { x: e.x, y: e.y, w: Math.max(...lines.map((l) => [...l].length)) * e.s * 0.62 + 4, h: lines.length * e.s * 1.25 };
  }
  return { x: e.x, y: e.y, w: e.w, h: e.h };
}
/** The box around all of `els` (an iterable), or null if there are none. */
export function boundsAll(els) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, n = 0;
  for (const e of els) { const b = bounds(e); n++; x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y); x1 = Math.max(x1, b.x + b.w); y1 = Math.max(y1, b.y + b.h); }
  return n ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}
/** Where a new note of w × h goes when no place is given (an agent's note): the first free spot in a column right of
 *  everything on the board, from its top, else the middle of an empty board. */
export function freeSpot(els, w = NOTE_W, h = NOTE_H) {
  const all = [...els], box = boundsAll(all);
  if (!box) return { x: -w / 2, y: -h / 2 };
  const boxes = all.map(bounds), x = Math.round(box.x + box.w + 40);
  const hit = (y) => boxes.some((b) => b.x < x + w + 20 && b.x + b.w > x - 20 && b.y < y + h + 20 && b.y + b.h > y - 20);
  let y = Math.round(box.y);
  for (let i = 0; i < 200 && hit(y); i++) y += 30;
  return { x, y };
}
